import axios, { AxiosError, AxiosInstance } from 'axios';
import { getApiErrorMessage } from '../utils/apiError';
import type {
  User,
  Organization,
  OrgApiKey,
  OrgApiKeyCreated,
  Trailer,
  TrailerEconomics,
  PeriodSummary,
  CalendarEntry,
  ManualCost,
  CvrCompanyInfo,
  ApiResponse,
  InviteCredentials,
  CargoCalendarResponse,
  OrganizationSettings,
  KoreseddelTemplate,
  GeneratedKoreseddel,
  KoreseddelCargo,
  BulkGenerateResult,
  BulkUploadResult,
  BulkUploadJob,
  TrailerCargo,
  RouteWithDetails,
  Driver,
  SplitRouteResult,
  WorkHourEntry,
  WorkActivityEntry,
  UpdateTripData,
  CostRule,
  RouteEconomics,
  DriverEconomics,
  CustomerEconomics,
  OrderEconomics,
  DriverMapping,
  FmsDevice,
  FmsComparisonData,
  PriceEstimateRequest,
  PriceEstimateResponse,
  PriceIntelligentRequest,
  PriceIntelligentResponse,
  CmrFieldsResponse,
  CmrFieldMapping,
  GeneratedCMR,
  CmrBulkGenerateResult,
  RouteDetailData,
  CostCategoryItem,
  Supplier,
  Quote,
  QuoteActivity,
  QuoteActivityKind,
  QuoteCreateInput,
  PublicQuoteView,
  OrderSchedule,
  OrderScheduleInput,
  OrderTemplate,
  OrderTemplateInput,
  EconomicInvoice,
  EconomicJournalEntry,
  EconomicSupplier,
  DatalonSalaryType,
  DatalonDriverRate,
  PriceEngineSuggestion,
  PriceEngineCustomerPrice,
  PriceEngineCustomerPriceInput,
  EmballageStatus,
  EmballageFraktionDef,
  EmballageCustomer,
  EmballageProduct,
  EmballageFacility,
  EmballageSavedReport,
} from '../types';

const API_BASE = '/api';

class ApiService {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('token');
    if (this.token) {
      this.setAuthHeader(this.token);
    }

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse<unknown>>) => {
        if (error.response?.status === 401) {
          this.logout();
          window.location.href = '/login';
        }
        // Axios' egen .message er altid "Request failed with status code NNN".
        // Serverens rigtige besked ligger i svarets error-felt, og over halvdelen
        // af komponenterne viser .message direkte - så de mistede den. Skriv den
        // rigtige tekst ind i .message, så alle visningssteder får den, uanset om
        // de graver i response.data.error eller ej.
        error.message = getApiErrorMessage(error);
        throw error;
      }
    );
  }

  private setAuthHeader(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  private clearAuthHeader() {
    delete this.client.defaults.headers.common['Authorization'];
  }

  // Auth
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    const response = await this.client.post<ApiResponse<{ token: string; user: User }>>(
      '/auth/login',
      { email, password }
    );
    if (response.data.data) {
      this.token = response.data.data.token;
      localStorage.setItem('token', this.token);
      this.setAuthHeader(this.token);
      return response.data.data;
    }
    throw new Error('Login failed');
  }

  logout() {
    this.token = null;
    localStorage.removeItem('token');
    this.clearAuthHeader();
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<ApiResponse<User>>('/auth/me');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get user');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.client.put('/auth/password', { currentPassword, newPassword });
  }

  // Admin - Organizations
  async getOrganizations(): Promise<Organization[]> {
    const response = await this.client.get<ApiResponse<Organization[]>>('/admin/organizations');
    return response.data.data || [];
  }

  async getOrganization(id: string): Promise<Organization> {
    const response = await this.client.get<ApiResponse<Organization>>(`/admin/organizations/${id}`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Organization not found');
  }

  async createOrganization(data: Omit<Organization, 'id' | 'createdAt' | 'updatedAt'> & { doraApiKey: string }): Promise<Organization> {
    const response = await this.client.post<ApiResponse<Organization>>('/admin/organizations', data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create organization');
  }

  async updateOrganization(id: string, data: Partial<Organization> & { doraApiKey?: string }): Promise<Organization> {
    const response = await this.client.put<ApiResponse<Organization>>(`/admin/organizations/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update organization');
  }

  async deleteOrganization(id: string): Promise<void> {
    await this.client.delete(`/admin/organizations/${id}`);
  }

  async testApiKey(organizationId: string): Promise<{ isValid: boolean; message: string; environment?: string }> {
    const response = await this.client.post<ApiResponse<{ isValid: boolean; message: string; environment?: string }>>(
      `/admin/organizations/${organizationId}/test-api-key`
    );
    return response.data.data || { isValid: false, message: 'Unknown error' };
  }

  // API-nøgler pr. organisation (til rental-app / public API-integrationer)
  async getOrgApiKeys(organizationId: string): Promise<OrgApiKey[]> {
    const r = await this.client.get<ApiResponse<OrgApiKey[]>>(`/admin/organizations/${organizationId}/api-keys`);
    return r.data.data || [];
  }

  async createOrgApiKey(organizationId: string, body: { name: string; scope?: string }): Promise<OrgApiKeyCreated> {
    const r = await this.client.post<ApiResponse<OrgApiKeyCreated>>(`/admin/organizations/${organizationId}/api-keys`, body);
    if (!r.data.data) throw new Error('Kunne ikke oprette nøgle');
    return r.data.data;
  }

  async revokeOrgApiKey(organizationId: string, keyId: string): Promise<void> {
    await this.client.delete(`/admin/organizations/${organizationId}/api-keys/${keyId}`);
  }

  async createInvite(organizationId: string, data: { email: string; name: string }): Promise<InviteCredentials> {
    const response = await this.client.post<ApiResponse<InviteCredentials>>(
      `/admin/organizations/${organizationId}/create-invite`,
      data
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create invite');
  }

  // Admin - Users
  async getUsers(): Promise<User[]> {
    const response = await this.client.get<ApiResponse<User[]>>('/admin/users');
    return response.data.data || [];
  }

  async createUser(data: { email: string; name: string; role?: 'SUPERADMIN' | 'ADMIN'; organizationId?: string; allowedMenuItems?: string[] }): Promise<{ user: User; credentials: { email: string; password: string } }> {
    const response = await this.client.post<ApiResponse<User> & { credentials: { email: string; password: string } }>('/admin/users', data);
    if (response.data.data) {
      return { user: response.data.data, credentials: response.data.credentials };
    }
    throw new Error('Failed to create user');
  }

  async updateUser(id: string, data: { email?: string; password?: string; name?: string; role?: 'SUPERADMIN' | 'ADMIN'; organizationId?: string | null; allowedMenuItems?: string[] }): Promise<User> {
    const response = await this.client.put<ApiResponse<User>>(`/admin/users/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update user');
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/admin/users/${id}`);
  }

  // Org-level user management
  async getOrgUsers(): Promise<User[]> {
    const response = await this.client.get<ApiResponse<User[]>>('/org-users');
    return response.data.data || [];
  }

  async createOrgUser(data: { email: string; name: string; allowedMenuItems?: string[]; payrollAccess?: boolean }): Promise<{ user: User; credentials: { email: string; password: string } }> {
    const response = await this.client.post<ApiResponse<User> & { credentials: { email: string; password: string } }>('/org-users', data);
    if (response.data.data) {
      return { user: response.data.data, credentials: response.data.credentials };
    }
    throw new Error('Failed to create user');
  }

  async updateOrgUser(id: string, data: { email?: string; password?: string; name?: string; allowedMenuItems?: string[]; payrollAccess?: boolean }): Promise<User> {
    const response = await this.client.put<ApiResponse<User>>(`/org-users/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update user');
  }

  async deleteOrgUser(id: string): Promise<void> {
    await this.client.delete(`/org-users/${id}`);
  }

  // CVR
  async lookupCvr(cvr: string): Promise<CvrCompanyInfo> {
    const response = await this.client.get<ApiResponse<CvrCompanyInfo>>('/cvr/lookup', { params: { cvr } });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('CVR lookup failed');
  }

  async searchCvr(name: string): Promise<CvrCompanyInfo[]> {
    const response = await this.client.get<ApiResponse<CvrCompanyInfo[]>>('/cvr/search', { params: { name } });
    return response.data.data || [];
  }

  // Trailers
  async getTrailers(): Promise<Trailer[]> {
    const response = await this.client.get<ApiResponse<Trailer[]>>('/trailers');
    return response.data.data || [];
  }

  async getTrailer(id: string): Promise<Trailer> {
    const response = await this.client.get<ApiResponse<Trailer>>(`/trailers/${id}`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Trailer not found');
  }

  async getTrailerEconomics(trailerId: string, from: string, to: string): Promise<TrailerEconomics> {
    const response = await this.client.get<ApiResponse<TrailerEconomics>>(
      `/trailers/${trailerId}/economics`,
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get trailer economics');
  }

  async getTrailerCargos(trailerId: string, from: string, to: string): Promise<TrailerCargo[]> {
    const response = await this.client.get<ApiResponse<TrailerCargo[]>>(
      `/trailers/${trailerId}/cargos`,
      { params: { from, to } }
    );
    return response.data.data || [];
  }

  // Analytics
  async getOverview(from?: string, to?: string, period?: string): Promise<PeriodSummary> {
    const response = await this.client.get<ApiResponse<PeriodSummary>>('/analytics/overview', {
      params: { from, to, period },
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get overview');
  }

  async getTrailerEconomicsList(from: string, to: string): Promise<{ trailers: TrailerEconomics[] }> {
    const response = await this.client.get<ApiResponse<{ trailers: TrailerEconomics[] }>>(
      '/analytics/trailer-economics',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get trailer economics');
  }

  async getOperationalKPIs(from: string, to: string, filter?: { trailerId?: string; customerNumber?: string }): Promise<{
    period: { from: string; to: string };
    totalCargos: number;
    deliveredTons: number;
    containerCount: number;
    craneHours: number;
    sludgeHours: number;
    totalStops: number;
    totalLoadmeters: number;
    byWeek: Array<{ week: string; deliveredTons: number; containerCount: number; craneHours: number; cargoCount: number }>;
  }> {
    const response = await this.client.get('/analytics/operational-kpis', { params: { from, to, ...filter } });
    return response.data.data;
  }

  async getTrailerHourEfficiency(from: string, to: string): Promise<{
    trailers: Array<{
      trailerId: string;
      trailerNumber: string;
      trailerAlias: string | null;
      effectiveHours: number;
      invoicedHours: number;
      invoicedRevenue: number;
      utilizationPct: number;
      avgHourRate: number;
      lostRevenue: number;
    }>;
  }> {
    const response = await this.client.get('/analytics/trailer-hour-efficiency', { params: { from, to } });
    return response.data.data || { trailers: [] };
  }

  async getRouteEconomicsList(from: string, to: string): Promise<{ routes: RouteEconomics[] }> {
    const response = await this.client.get<ApiResponse<{ routes: RouteEconomics[] }>>(
      '/analytics/route-economics',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get route economics');
  }

  async getRouteDetail(trailerId: string, routeId: string): Promise<RouteDetailData> {
    const response = await this.client.get<ApiResponse<RouteDetailData>>(
      `/analytics/route-detail/${trailerId}/${routeId}`
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get route detail');
  }

  async getDriverEconomicsList(from: string, to: string): Promise<{ drivers: DriverEconomics[] }> {
    const response = await this.client.get<ApiResponse<{ drivers: DriverEconomics[] }>>(
      '/analytics/driver-economics',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get driver economics');
  }

  async getCustomerEconomicsList(from: string, to: string): Promise<{ customers: CustomerEconomics[] }> {
    const response = await this.client.get<ApiResponse<{ customers: CustomerEconomics[] }>>(
      '/analytics/customer-economics',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get customer economics');
  }

  async getOrderEconomicsList(from: string, to: string): Promise<{ orders: OrderEconomics[] }> {
    const response = await this.client.get<ApiResponse<{ orders: OrderEconomics[] }>>(
      '/analytics/order-economics',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get order economics');
  }

  async getRevenueByPeriod(from: string, to: string, groupBy: 'day' | 'week' | 'month' = 'day'): Promise<Array<{ period: string; revenue: number; kilometers: number }>> {
    const response = await this.client.get<ApiResponse<Array<{ period: string; revenue: number; kilometers: number }>>>(
      '/analytics/revenue-by-period',
      { params: { from, to, groupBy } }
    );
    return response.data.data || [];
  }

  // Calendar
  async getCalendarUtilization(from: string, to: string): Promise<{ entries: CalendarEntry[]; utilizationRate: number; byDate: Record<string, CalendarEntry[]> }> {
    const response = await this.client.get<ApiResponse<{ entries: CalendarEntry[]; utilizationRate: number; byDate: Record<string, CalendarEntry[]> }>>(
      '/calendar/utilization',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get calendar data');
  }

  async getTrailerCalendar(trailerId: string, from: string, to: string) {
    const response = await this.client.get(`/calendar/trailer/${trailerId}`, { params: { from, to } });
    return response.data.data;
  }

  async getCargoCalendar(from: string, to: string): Promise<CargoCalendarResponse> {
    const response = await this.client.get<ApiResponse<CargoCalendarResponse>>(
      '/calendar/cargo-overview',
      { params: { from, to } }
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get cargo calendar');
  }

  // Manual Costs
  async getCosts(params?: { trailerId?: string; routeId?: string; from?: string; to?: string }): Promise<ManualCost[]> {
    const response = await this.client.get<ApiResponse<ManualCost[]>>('/costs', { params });
    return response.data.data || [];
  }

  async createCost(data: Omit<ManualCost, 'id' | 'organizationId' | 'createdAt'>): Promise<ManualCost> {
    const response = await this.client.post<ApiResponse<ManualCost>>('/costs', data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to create cost');
  }

  async updateCost(id: string, data: Partial<Omit<ManualCost, 'id' | 'organizationId' | 'createdAt'>>): Promise<ManualCost> {
    const response = await this.client.put<ApiResponse<ManualCost>>(`/costs/${id}`, data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update cost');
  }

  async deleteCost(id: string): Promise<void> {
    await this.client.delete(`/costs/${id}`);
  }

  // Export
  getExportCsvUrl(from: string, to: string): string {
    return `${API_BASE}/export/csv/trailer-economics?from=${from}&to=${to}`;
  }

  getExportPdfTrailerUrl(trailerId: string, from: string, to: string): string {
    return `${API_BASE}/export/pdf/trailer-report/${trailerId}?from=${from}&to=${to}`;
  }

  getExportPdfPeriodUrl(from: string, to: string): string {
    return `${API_BASE}/export/pdf/period-report?from=${from}&to=${to}`;
  }

  // Settings
  async getSettings(): Promise<OrganizationSettings> {
    const response = await this.client.get<ApiResponse<OrganizationSettings>>('/settings');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get settings');
  }

  async updateSettings(data: Partial<OrganizationSettings>): Promise<OrganizationSettings> {
    const response = await this.client.put<ApiResponse<OrganizationSettings>>('/settings', data);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update settings');
  }

  async getTeams(): Promise<Array<{ id: string; name: string }>> {
    const response = await this.client.get<ApiResponse<Array<{ id: string; name: string }>>>('/settings/teams');
    return response.data.data || [];
  }

  async syncFromDora(): Promise<void> {
    await this.client.post('/settings/sync');
  }

  async uploadLogo(file: File): Promise<{ hasLogo: boolean; logoMime: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await this.client.post<ApiResponse<{ hasLogo: boolean; logoMime: string }>>('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data.success) {
      return response.data.data!;
    }
    throw new Error('Failed to upload logo');
  }

  async deleteLogo(): Promise<void> {
    await this.client.delete('/settings/logo');
  }

  async getLogoBlob(): Promise<Blob | null> {
    try {
      const response = await this.client.get('/settings/logo', {
        responseType: 'blob',
      });
      return response.data;
    } catch {
      return null;
    }
  }

  async getKoreseddelTemplates(): Promise<KoreseddelTemplate[]> {
    const response = await this.client.get<ApiResponse<KoreseddelTemplate[]>>('/settings/templates');
    return response.data.data || [];
  }

  // Cargo type colors for calendar
  async getCargoTypeColors(): Promise<{
    colors: Record<string, string>;
    customColors: Record<string, string>;
    defaultColors: Record<string, string>;
  }> {
    const response = await this.client.get<ApiResponse<{
      colors: Record<string, string>;
      customColors: Record<string, string>;
      defaultColors: Record<string, string>;
    }>>('/settings/cargo-colors');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get cargo type colors');
  }

  async updateCargoTypeColors(colors: Record<string, string>): Promise<Record<string, string>> {
    const response = await this.client.put<ApiResponse<Record<string, string>>>('/settings/cargo-colors', { colors });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to update cargo type colors');
  }

  async resetCargoTypeColors(): Promise<Record<string, string>> {
    const response = await this.client.delete<ApiResponse<Record<string, string>>>('/settings/cargo-colors');
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to reset cargo type colors');
  }

  getTemplatePreviewUrl(templateId: string): string {
    return `${API_BASE}/koresedler/templates/${templateId}/preview`;
  }

  async viewTemplatePreview(templateId: string): Promise<void> {
    const response = await this.client.get(`/koresedler/templates/${templateId}/preview`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Køresedler
  async getKoresedler(): Promise<GeneratedKoreseddel[]> {
    const response = await this.client.get<ApiResponse<GeneratedKoreseddel[]>>('/koresedler');
    return response.data.data || [];
  }

  async getKoreseddelCargos(from?: string, to?: string): Promise<KoreseddelCargo[]> {
    const response = await this.client.get<ApiResponse<KoreseddelCargo[]>>('/koresedler/cargos', {
      params: { from, to },
    });
    return response.data.data || [];
  }

  async generateKoreseddel(cargoId: string, templateId: string): Promise<GeneratedKoreseddel> {
    const response = await this.client.post<ApiResponse<GeneratedKoreseddel>>('/koresedler/generate', {
      cargoId,
      templateId,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to generate køreseddel');
  }

  getKoreseddelDownloadUrl(id: string): string {
    return `${API_BASE}/koresedler/${id}/download`;
  }

  getKoreseddelViewUrl(id: string): string {
    return `${API_BASE}/koresedler/${id}/view`;
  }

  async viewKoreseddelPdf(id: string): Promise<void> {
    const response = await this.client.get(`/koresedler/${id}/view`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank');
    // Clean up after a delay to allow the new window to load
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async downloadKoreseddelPdf(id: string, filename: string): Promise<void> {
    const response = await this.client.get(`/koresedler/${id}/download`, {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }

  async uploadKoreseddelToDora(id: string): Promise<GeneratedKoreseddel> {
    const response = await this.client.post<ApiResponse<GeneratedKoreseddel>>(`/koresedler/${id}/upload-to-dora`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to upload to DORA');
  }

  async deleteKoreseddel(id: string): Promise<void> {
    await this.client.delete(`/koresedler/${id}`);
  }

  async bulkGenerateKoresedler(cargoIds: string[], templateId: string): Promise<BulkGenerateResult> {
    const response = await this.client.post<ApiResponse<BulkGenerateResult>>('/koresedler/bulk-generate', {
      cargoIds,
      templateId,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to bulk generate køresedler');
  }

  async bulkUploadKoresedler(koreseddelIds: string[]): Promise<BulkUploadResult> {
    const response = await this.client.post<ApiResponse<BulkUploadResult>>('/koresedler/bulk-upload', {
      koreseddelIds,
    });
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to bulk upload køresedler');
  }

  // Retroactive Bulk Upload Job (generate and upload for historical orders)
  async startBulkUploadJob(fromDate: string, templateId: string, skipExisting: boolean = true): Promise<string> {
    const response = await this.client.post<ApiResponse<{ jobId: string }>>('/koresedler/bulk-upload-job', {
      fromDate,
      templateId,
      skipExisting,
    });
    if (response.data.data?.jobId) {
      return response.data.data.jobId;
    }
    throw new Error('Failed to start bulk upload job');
  }

  async getBulkUploadJobStatus(jobId: string): Promise<BulkUploadJob> {
    const response = await this.client.get<ApiResponse<BulkUploadJob>>(`/koresedler/bulk-upload-job/${jobId}/status`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to get job status');
  }

  async getBulkUploadJobs(): Promise<BulkUploadJob[]> {
    const response = await this.client.get<ApiResponse<BulkUploadJob[]>>('/koresedler/bulk-upload-job/jobs');
    return response.data.data || [];
  }

  // Split cargo into 3 orders with reload location
  async splitCargo(
    cargoId: string,
    params: {
      reloadAddress: {
        address: string;
        city: string;
        postcode: string;
        country: string;
        lat?: number;
        lon?: number;
        reloadDate?: string;
      };
      trailerId?: string;
      deliveryRouteOption?: 'undispatched' | 'newRoute';
      deliveryRouteDriverId?: string;
      deliveryRouteTrailerId?: string;
    }
  ): Promise<{
    originalCargoId: string;
    originalRef: string;
    route1Id: string | null;
    newCargo1: { id: string; ref: string } | null;
    newCargo3: { id: string; ref: string };
    route3Id: string | null;
    originalRoute: string;
    tag2Applied: boolean;
  }> {
    const response = await this.client.post<ApiResponse<{
      originalCargoId: string;
      originalRef: string;
      route1Id: string | null;
      newCargo1: { id: string; ref: string } | null;
      newCargo3: { id: string; ref: string };
      route3Id: string | null;
      originalRoute: string;
      tag2Applied: boolean;
    }>>(`/cargos/${cargoId}/split`, params);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to split cargo');
  }

  // Undo a cargo split - delete the 2 new orders and restore the original
  async unsplitCargo(cargoId: string): Promise<{
    originalCargoId: string;
    originalRef: string;
    deletedCargo2Ref: string | null;
    deletedCargo3Ref: string | null;
  }> {
    const response = await this.client.post<ApiResponse<{
      originalCargoId: string;
      originalRef: string;
      deletedCargo2Ref: string | null;
      deletedCargo3Ref: string | null;
    }>>(`/cargos/${cargoId}/unsplit`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to unsplit cargo');
  }

  // Sync documents across all orders in a split group
  async syncCargoDocuments(cargoId: string): Promise<{
    cargosInSplit: number;
    totalDocuments: number;
    documentsSynced: number;
  }> {
    const response = await this.client.post<ApiResponse<{
      cargosInSplit: number;
      totalDocuments: number;
      documentsSynced: number;
    }>>(`/cargos/${cargoId}/sync-documents`);
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to sync documents');
  }

  // Routes
  async getRoutes(from?: string, to?: string): Promise<RouteWithDetails[]> {
    const response = await this.client.get<ApiResponse<RouteWithDetails[]>>('/routes', {
      params: { from, to },
    });
    return response.data.data || [];
  }

  async getDrivers(): Promise<Driver[]> {
    const response = await this.client.get<ApiResponse<Driver[]>>('/routes/drivers');
    return response.data.data || [];
  }

  async splitRoute(
    routeId: string,
    params: {
      trailerId: string;
      cargoIds?: string[];
      reloadAddress: {
        address: string;
        city: string;
        postcode: string;
        country: string;
        lat?: number;
        lon?: number;
        reloadDate?: string;
      };
      deliveryRouteOption: 'undispatched' | 'newRoute';
      deliveryRouteDriverId?: string;
      deliveryRouteTrailerId?: string;
    }
  ): Promise<SplitRouteResult> {
    const response = await this.client.post<ApiResponse<SplitRouteResult>>(
      `/routes/${routeId}/split`,
      params
    );
    if (response.data.data) {
      return response.data.data;
    }
    throw new Error('Failed to split route');
  }
  // Cost Categories
  async getCostCategories(): Promise<CostCategoryItem[]> {
    const response = await this.client.get<ApiResponse<CostCategoryItem[]>>('/cost-categories');
    return response.data.data || [];
  }

  async createCostCategory(data: { key: string; name: string; color?: string }): Promise<CostCategoryItem> {
    const response = await this.client.post<ApiResponse<CostCategoryItem>>('/cost-categories', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create cost category');
  }

  async updateCostCategory(id: string, data: { name?: string; color?: string; sortOrder?: number }): Promise<CostCategoryItem> {
    const response = await this.client.put<ApiResponse<CostCategoryItem>>(`/cost-categories/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update cost category');
  }

  async deleteCostCategory(id: string): Promise<void> {
    await this.client.delete(`/cost-categories/${id}`);
  }

  // Suppliers
  async getSuppliers(): Promise<Supplier[]> {
    const response = await this.client.get<ApiResponse<Supplier[]>>('/suppliers');
    return response.data.data || [];
  }

  async createSupplier(data: { name: string; cvr?: string | null }): Promise<Supplier> {
    const response = await this.client.post<ApiResponse<Supplier>>('/suppliers', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create supplier');
  }

  async updateSupplier(id: string, data: { name?: string; cvr?: string | null }): Promise<Supplier> {
    const response = await this.client.put<ApiResponse<Supplier>>(`/suppliers/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update supplier');
  }

  async deleteSupplier(id: string): Promise<void> {
    await this.client.delete(`/suppliers/${id}`);
  }

  // Cost Rules
  async getCostRules(params?: { trailerId?: string; isActive?: boolean }): Promise<CostRule[]> {
    const response = await this.client.get<ApiResponse<CostRule[]>>('/cost-rules', { params });
    return response.data.data || [];
  }

  async createCostRule(data: Omit<CostRule, 'id' | 'organizationId' | 'createdAt' | 'updatedAt'>): Promise<CostRule> {
    const response = await this.client.post<ApiResponse<CostRule>>('/cost-rules', data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create cost rule');
  }

  async updateCostRule(id: string, data: Partial<CostRule>): Promise<CostRule> {
    const response = await this.client.put<ApiResponse<CostRule>>(`/cost-rules/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update cost rule');
  }

  async deleteCostRule(id: string): Promise<void> {
    await this.client.delete(`/cost-rules/${id}`);
  }

  // Work Hours
  async getWorkHours(from: string, to: string): Promise<{ entries: WorkHourEntry[]; lastSyncedAt: string | null; needsSync: boolean }> {
    const response = await this.client.get<ApiResponse<WorkHourEntry[]> & { lastSyncedAt: string | null; needsSync: boolean }>(
      '/work-hours',
      { params: { from, to } }
    );
    return {
      entries: response.data.data || [],
      lastSyncedAt: response.data.lastSyncedAt || null,
      needsSync: response.data.needsSync ?? false,
    };
  }

  async syncWorkHours(): Promise<{ synced: number; skipped: number; total: number }> {
    const response = await this.client.post<ApiResponse<null> & { synced: number; skipped: number; total: number }>(
      '/work-hours/sync'
    );
    return {
      synced: response.data.synced,
      skipped: response.data.skipped,
      total: response.data.total,
    };
  }

  // Work activities (chauffør-app pause/værksted/vask via X-API-Key)
  async getWorkActivities(
    from: string,
    to: string,
    filters?: { driverId?: string; kind?: 'BREAK' | 'WORKSHOP' | 'WASHING' | 'OTHER' },
  ): Promise<{
    entries: WorkActivityEntry[];
    totals: { byKind: Record<string, number>; minutes: number };
    drivers: Array<{ id: string; name: string }>;
  }> {
    const response = await this.client.get<
      ApiResponse<WorkActivityEntry[]> & {
        totals: { byKind: Record<string, number>; minutes: number };
        drivers: Array<{ id: string; name: string }>;
      }
    >('/work-activities', { params: { from, to, ...filters } });
    return {
      entries: response.data.data || [],
      totals: response.data.totals,
      drivers: response.data.drivers,
    };
  }

  async patchWorkActivity(
    id: string,
    body: { startedAt?: string; endedAt?: string | null; notes?: string | null; kind?: string; customLabel?: string | null },
  ): Promise<WorkActivityEntry> {
    const response = await this.client.patch<ApiResponse<WorkActivityEntry>>(`/work-activities/${id}`, body);
    return response.data.data!;
  }

  async deleteWorkActivity(id: string): Promise<void> {
    await this.client.delete(`/work-activities/${id}`);
  }

  async syncAllWorkHours(): Promise<{ synced: number; skipped: number; total: number; dateRange: { from: string; to: string } }> {
    const response = await this.client.post<ApiResponse<null> & { synced: number; skipped: number; total: number; dateRange: { from: string; to: string } }>(
      '/work-hours/sync-all'
    );
    return {
      synced: response.data.synced,
      skipped: response.data.skipped,
      total: response.data.total,
      dateRange: response.data.dateRange,
    };
  }

  async createWorkHourEntry(data: {
    driverId: string;
    driverName: string;
    trailerId?: string;
    trailerNumber?: string;
    trailerAlias?: string | null;
    date: string;
    start?: { dateTime: string; odometer?: string; note?: string | null };
    end?: { dateTime: string; odometer?: string; note?: string | null };
    pauseMinutes?: number;
  }): Promise<{ id: string; routeId: string }> {
    const response = await this.client.post<ApiResponse<{ id: string; routeId: string }>>('/work-hours', data);
    return response.data.data!;
  }

  async deleteWorkHourEntry(id: string): Promise<void> {
    await this.client.delete(`/work-hours/${id}`);
  }

  async updateTrip(routeId: string, data: UpdateTripData): Promise<void> {
    await this.client.put(`/work-hours/${routeId}/trip`, data);
  }

  // Opdater alle records for samme (driverName, date) - kaskader rettelse
  // saa WORKDAY + DORA_TMS-rakker er konsistente.
  async updateWorkDay(data: {
    driverId: string;
    date: string;
    start?: { dateTime: string };
    end?: { dateTime: string };
    pauseMinutes?: number;
  }): Promise<void> {
    await this.client.patch('/work-hours/day', data);
  }

  // Driver mappings
  async getDriverMappings(): Promise<DriverMapping[]> {
    const response = await this.client.get('/settings/driver-mappings');
    return response.data.data;
  }

  async updateDriverMappings(mappings: DriverMapping[]): Promise<void> {
    await this.client.put('/settings/driver-mappings', { mappings });
  }

  async getFmsDevices(): Promise<FmsDevice[]> {
    const response = await this.client.get('/settings/fms-devices');
    return response.data.data;
  }

  // FMS Comparison
  async getFmsComparison(from: string, to: string): Promise<FmsComparisonData> {
    const response = await this.client.get('/work-hours/fms-comparison', {
      params: { from, to },
    });
    return response.data.data;
  }

  // Send invite email
  /// Sender enten en invitation eller en nulstilling, alt efter om brugeren
  /// har logget ind før. Serveren afgør det og svarer med hvilken der blev sendt.
  async sendInvite(userId: string): Promise<{ message: string; kind?: 'INVITE' | 'RESET' }> {
    const response = await this.client.post(`/admin/users/${userId}/send-invite`);
    return response.data;
  }

  // Set password with invite token (no auth)
  async setPasswordWithToken(token: string, password: string): Promise<void> {
    await axios.post(`${API_BASE}/auth/set-password`, { token, password });
  }

  // Selvbetjent nulstilling (no auth). Svarer ens uanset om adressen findes.
  async requestPasswordReset(email: string): Promise<void> {
    await axios.post(`${API_BASE}/auth/forgot-password`, { email });
  }

  // Danløn CSV download
  async downloadDanlonCsv(from: string, to: string): Promise<void> {
    const response = await this.client.get('/export/csv/danlon-work-hours', {
      params: { from, to },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `danlon_timer_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Dataløn Branche CSV download
  async downloadDatalonBrancheCsv(from: string, to: string): Promise<void> {
    const response = await this.client.get('/export/csv/datalon-branche-work-hours', {
      params: { from, to },
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `datalon_branche_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // ============================================================
  // Dataløn Branche — lønart-katalog og chauffør-satser
  // ============================================================
  async getDatalonSalaryTypes(): Promise<DatalonSalaryType[]> {
    const r = await this.client.get<{ data: DatalonSalaryType[] }>('/datalon/salary-types');
    return r.data.data;
  }
  async createDatalonSalaryType(data: Omit<DatalonSalaryType, 'id' | 'createdAt' | 'updatedAt'>): Promise<DatalonSalaryType> {
    const r = await this.client.post<{ data: DatalonSalaryType }>('/datalon/salary-types', data);
    return r.data.data;
  }
  async updateDatalonSalaryType(id: string, data: Partial<DatalonSalaryType>): Promise<DatalonSalaryType> {
    const r = await this.client.put<{ data: DatalonSalaryType }>(`/datalon/salary-types/${id}`, data);
    return r.data.data;
  }
  async deleteDatalonSalaryType(id: string): Promise<void> {
    await this.client.delete(`/datalon/salary-types/${id}`);
  }
  async getDatalonDriverRates(): Promise<DatalonDriverRate[]> {
    const r = await this.client.get<{ data: DatalonDriverRate[] }>('/datalon/driver-rates');
    return r.data.data;
  }
  async upsertDatalonDriverRate(driverId: string, salaryTypeId: string, rate: number): Promise<DatalonDriverRate> {
    const r = await this.client.post<{ data: DatalonDriverRate }>('/datalon/driver-rates', { driverId, salaryTypeId, rate });
    return r.data.data;
  }
  async deleteDatalonDriverRate(driverId: string, salaryTypeId: string): Promise<void> {
    await this.client.delete(`/datalon/driver-rates/${driverId}/${salaryTypeId}`);
  }

  // ============================================================
  // Price Engine — prismotor mod DORA-cargos
  // ============================================================
  async getPriceEngineConfig(): Promise<{ priceEngineEnabled: boolean; priceEngineTimeRateProductNumbers: string[] }> {
    const r = await this.client.get<{ data: { priceEngineEnabled: boolean; priceEngineTimeRateProductNumbers: string[] } }>('/price-engine/config');
    return r.data.data;
  }
  async updatePriceEngineConfig(data: { priceEngineEnabled?: boolean; priceEngineTimeRateProductNumbers?: string[] }) {
    const r = await this.client.put<{ data: { priceEngineEnabled: boolean; priceEngineTimeRateProductNumbers: string[] } }>('/price-engine/config', data);
    return r.data.data;
  }
  async getPriceEngineSuggestions(status?: string): Promise<PriceEngineSuggestion[]> {
    const r = await this.client.get<{ data: PriceEngineSuggestion[] }>('/price-engine/suggestions', { params: status ? { status } : {} });
    return r.data.data || [];
  }
  async scanPriceEngineNow(): Promise<{ scanned: number; suggestions: number }> {
    const r = await this.client.post<{ data: { scanned: number; suggestions: number } }>('/price-engine/scan-now');
    return r.data.data;
  }
  async approvePriceEngineSuggestion(id: string): Promise<void> {
    await this.client.post(`/price-engine/suggestions/${id}/approve`);
  }
  async rejectPriceEngineSuggestion(id: string): Promise<void> {
    await this.client.post(`/price-engine/suggestions/${id}/reject`);
  }
  async bulkApprovePriceEngineSuggestions(ids: string[]): Promise<{ results: Array<{ id: string; ok: boolean; error?: string }>; ok: number; failed: number }> {
    const r = await this.client.post<{ data: { results: Array<{ id: string; ok: boolean; error?: string }>; ok: number; failed: number } }>('/price-engine/suggestions/bulk-approve', { ids });
    return r.data.data;
  }
  async getPriceEngineCustomerPrices(customerNumber?: string): Promise<PriceEngineCustomerPrice[]> {
    const r = await this.client.get<{ data: PriceEngineCustomerPrice[] }>('/price-engine/customer-prices', {
      params: customerNumber ? { customerNumber } : {},
    });
    return r.data.data;
  }
  async upsertPriceEngineCustomerPrice(data: PriceEngineCustomerPriceInput): Promise<PriceEngineCustomerPrice> {
    const r = await this.client.post<{ data: PriceEngineCustomerPrice }>('/price-engine/customer-prices', data);
    return r.data.data;
  }
  async deletePriceEngineCustomerPrice(id: string): Promise<void> {
    await this.client.delete(`/price-engine/customer-prices/${id}`);
  }
  async bulkImportPriceEngineCustomerPrices(prices: PriceEngineCustomerPriceInput[]): Promise<{ created: number; updated: number; total: number }> {
    const r = await this.client.post<{ data: { created: number; updated: number; total: number } }>(
      '/price-engine/customer-prices/bulk-import',
      { prices },
    );
    return r.data.data;
  }
  // Work hours Excel download (detailed - one sheet per driver)
  async downloadWorkHoursExcel(from: string, to: string, driverIds?: string[]): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (driverIds && driverIds.length > 0) {
      params.driverIds = driverIds.join(',');
    }
    const response = await this.client.get('/export/excel/work-hours', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arbejdstimer_${from}_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Work hours Excel summary download (one sheet, one row per driver)
  async downloadWorkHoursSummaryExcel(from: string, to: string, driverIds?: string[]): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (driverIds && driverIds.length > 0) {
      params.driverIds = driverIds.join(',');
    }
    const response = await this.client.get('/export/excel/work-hours-summary', {
      params,
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arbejdstimer_oversigt_${from}_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Cost Report Export
  async downloadCostReportCsv(from: string, to: string, categories?: string, supplierIds?: string): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (categories) params.categories = categories;
    if (supplierIds) params.supplierIds = supplierIds;
    const response = await this.client.get('/export/csv/cost-report', { params, responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'text/csv; charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omkostningsrapport_${from}_${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async downloadCostReportExcel(from: string, to: string, categories?: string, supplierIds?: string): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (categories) params.categories = categories;
    if (supplierIds) params.supplierIds = supplierIds;
    const response = await this.client.get('/export/excel/cost-report', { params, responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omkostningsrapport_${from}_${to}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async downloadCostReportPdf(from: string, to: string, categories?: string, supplierIds?: string): Promise<void> {
    const params: Record<string, string> = { from, to };
    if (categories) params.categories = categories;
    if (supplierIds) params.supplierIds = supplierIds;
    const response = await this.client.get('/export/pdf/cost-report', { params, responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `omkostningsrapport_${from}_${to}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Price Calculator
  async getPriceCalculatorCustomers(): Promise<Array<{ client: string; clientNumber: string }>> {
    const response = await this.client.get('/price-calculator/customers');
    return response.data;
  }

  async calculatePriceEstimate(data: PriceEstimateRequest): Promise<PriceEstimateResponse> {
    const response = await this.client.post('/price-calculator/estimate', data);
    return response.data;
  }

  async calculatePriceIntelligent(data: PriceIntelligentRequest): Promise<PriceIntelligentResponse> {
    const response = await this.client.post('/price-calculator/intelligent', data);
    return response.data;
  }

  // CMR
  async getCmrFields(): Promise<CmrFieldsResponse> {
    const response = await this.client.get('/cmr/fields');
    return response.data;
  }

  async getCmrMappings(): Promise<CmrFieldMapping[]> {
    const response = await this.client.get('/cmr/mappings');
    return response.data;
  }

  async updateCmrMappings(mappings: CmrFieldMapping[]): Promise<CmrFieldMapping[]> {
    const response = await this.client.put('/cmr/mappings', mappings);
    return response.data;
  }

  async generateCmr(cargoId: string): Promise<GeneratedCMR> {
    const response = await this.client.post('/cmr/generate', { cargoId });
    return response.data;
  }

  async bulkGenerateCmr(cargoIds: string[]): Promise<CmrBulkGenerateResult> {
    const response = await this.client.post('/cmr/bulk-generate', { cargoIds });
    return response.data;
  }

  async downloadCmrPdf(cmrId: string, filename: string): Promise<void> {
    const response = await this.client.get(`/cmr/${cmrId}/pdf`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async getCmrList(): Promise<GeneratedCMR[]> {
    const response = await this.client.get('/cmr/list');
    return response.data;
  }

  // Quotes
  async getQuotes(params?: { status?: string }): Promise<Quote[]> {
    const response = await this.client.get<ApiResponse<Quote[]>>('/quotes', { params });
    return response.data.data || [];
  }

  async getQuote(id: string): Promise<Quote> {
    const response = await this.client.get<ApiResponse<Quote>>(`/quotes/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Tilbud ikke fundet');
  }

  async createQuote(data: QuoteCreateInput): Promise<Quote> {
    const response = await this.client.post<ApiResponse<Quote>>('/quotes', data);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke oprette tilbud');
  }

  async updateQuote(id: string, data: Partial<QuoteCreateInput>): Promise<Quote> {
    const response = await this.client.put<ApiResponse<Quote>>(`/quotes/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke opdatere tilbud');
  }

  async deleteQuote(id: string): Promise<void> {
    await this.client.delete(`/quotes/${id}`);
  }

  // Opretter et nyt DRAFT-tilbud som kopi af et eksisterende.
  // Returnerer det nye tilbud saa client kan navigere direkte til editoren.
  async copyQuote(id: string): Promise<Quote> {
    const r = await this.client.post<{ data: Quote }>(`/quotes/${id}/copy`);
    return r.data.data;
  }

  async getQuoteActivities(quoteId: string): Promise<QuoteActivity[]> {
    const r = await this.client.get<{ data: QuoteActivity[] }>(`/quotes/${quoteId}/activities`);
    return r.data.data;
  }

  async addQuoteActivity(quoteId: string, body: string, kind: QuoteActivityKind = 'NOTE'): Promise<QuoteActivity> {
    const r = await this.client.post<{ data: QuoteActivity }>(`/quotes/${quoteId}/activities`, { body, kind });
    return r.data.data;
  }

  async deleteQuoteActivity(quoteId: string, activityId: string): Promise<void> {
    await this.client.delete(`/quotes/${quoteId}/activities/${activityId}`);
  }

  async sendQuote(id: string): Promise<Quote> {
    const response = await this.client.post<ApiResponse<Quote>>(`/quotes/${id}/send`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke sende tilbud');
  }

  async convertQuote(id: string): Promise<{ quote: Quote; doraCargoId: string; doraCargoRef: string }> {
    const response = await this.client.post<ApiResponse<{ quote: Quote; doraCargoId: string; doraCargoRef: string }>>(`/quotes/${id}/convert`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke konvertere tilbud');
  }

  async acceptQuote(id: string): Promise<{ quote: Quote; templateIds: string[] }> {
    const response = await this.client.post<ApiResponse<{ quote: Quote; templateIds: string[] }>>(`/quotes/${id}/accept`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke markere tilbud som accepteret');
  }

  async downloadQuotePdf(id: string, quoteNumber: number): Promise<void> {
    const response = await this.client.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Tilbud-${quoteNumber}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // Henter en .eml-fil og aabner den i brugerens mail-klient. Markerer
  // tilbuddet som SENT paa server-siden samtidig.
  async downloadQuoteEml(id: string, quoteNumber: number): Promise<void> {
    const response = await this.client.get(`/quotes/${id}/eml`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'message/rfc822' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Tilbud-${quoteNumber}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async getQuoteCustomers(): Promise<
    Array<{
      client: string;
      clientNumber: string;
      clientPhone?: string | null;
      clientEmail?: string | null;
      clientAddress?: string | null;
    }>
  > {
    const response = await this.client.get('/quotes/customers');
    return response.data.data || [];
  }

  async getQuoteTeams(): Promise<Array<{ id: string; name: string }>> {
    const response = await this.client.get('/quotes/teams');
    return response.data.data || [];
  }

  async getQuoteTeamCargoTypes(teamId: string): Promise<string[]> {
    const response = await this.client.get<ApiResponse<string[]>>(`/quotes/teams/${teamId}/cargo-types`);
    return response.data.data || [];
  }

  async getQuoteLabels(): Promise<Array<{ id: string; name: string }>> {
    const response = await this.client.get('/quotes/labels');
    return response.data.data || [];
  }

  async getQuoteProducts(): Promise<{
    products: Array<{ id: string; productNumber: string; name: string; unitId: string | null; salesPrice: string | null }>;
    surcharges: Array<{ id: string; productNumber: string; name: string; percentage: string | null }>;
    units: Array<{ id: string; name: string }>;
  }> {
    const response = await this.client.get('/quotes/products');
    return response.data.data || { products: [], surcharges: [], units: [] };
  }

  async followupQuote(id: string): Promise<Quote> {
    const response = await this.client.post<ApiResponse<Quote>>(`/quotes/${id}/followup-now`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke sende opfølgning');
  }

  async rejectQuote(id: string, rejectedReason?: string | null, customerComment?: string | null): Promise<Quote> {
    const response = await this.client.post<ApiResponse<Quote>>(`/quotes/${id}/reject`, { rejectedReason, customerComment });
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke afvise tilbud');
  }

  async updateQuoteNote(id: string, internalNote: string | null): Promise<Quote> {
    const response = await this.client.patch<ApiResponse<Quote>>(`/quotes/${id}/note`, { internalNote });
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke opdatere note');
  }

  // Quote catalog (DORA products + local category mapping)
  async getQuoteCatalog(): Promise<Array<{
    id: string;
    productNumber: string;
    name: string;
    salesPrice: string | null;
    category: string | null;
    defaultQuantity: number | null;
  }>> {
    const response = await this.client.get('/quote-catalog');
    return response.data.data || [];
  }

  async setQuoteCatalogCategory(doraProductId: string, category: string, defaultQuantity?: number | null): Promise<void> {
    await this.client.put(`/quote-catalog/${doraProductId}`, { category, defaultQuantity });
  }

  async deleteQuoteCatalogCategory(doraProductId: string): Promise<void> {
    await this.client.delete(`/quote-catalog/${doraProductId}`);
  }

  // Quote templates
  async getQuoteTemplates(customerNumber?: string): Promise<Array<{
    id: string;
    name: string;
    customerNumber: string | null;
    quoteType: string;
    cargoType: string | null;
    description: string | null;
    description2: string | null;
    note: string | null;
    adr: boolean;
    adrText: string | null;
    teamId: string | null;
    labelId: string | null;
    requirements: string[];
    qualities: string[];
    pickups: any[];
    dropoffs: any[];
    priceLines: any[];
    createdAt: string;
    updatedAt: string;
  }>> {
    const response = await this.client.get('/quote-templates', { params: customerNumber ? { customerNumber } : {} });
    return response.data.data || [];
  }

  async createQuoteTemplate(data: any): Promise<any> {
    const response = await this.client.post('/quote-templates', data);
    return response.data.data;
  }

  async deleteQuoteTemplate(id: string): Promise<void> {
    await this.client.delete(`/quote-templates/${id}`);
  }

  async estimateQuoteMargin(input: {
    trailerId?: string | null;
    distanceKm?: number | null;
    hours?: number | null;
    calendarDays?: number;
    totalRevenue: number;
    waypoints?: Array<{ lat: number; lon: number }>;
    invoiceLineCount?: number;
  }): Promise<{
    kilometers: number;
    hours: number;
    totalCost: number;
    totalRevenue: number;
    dg: number;
    dgPct: number;
    breakdown: Array<{ ruleName: string; amount: number; unitLabel: string; units: number }>;
    source: 'auto' | 'manual' | 'mixed';
  }> {
    const response = await this.client.post('/quotes/estimate-margin', input);
    return response.data.data;
  }

  // Order Schedules (Ordreskemaer)
  async getOrderSchedules(params?: { status?: string }): Promise<OrderSchedule[]> {
    const response = await this.client.get<ApiResponse<OrderSchedule[]>>('/order-schedules', { params });
    return response.data.data || [];
  }

  async getOrderSchedule(id: string): Promise<OrderSchedule> {
    const response = await this.client.get<ApiResponse<OrderSchedule>>(`/order-schedules/${id}`);
    if (response.data.data) return response.data.data;
    throw new Error('Ordreskema ikke fundet');
  }

  async createOrderSchedule(data: OrderScheduleInput): Promise<OrderSchedule> {
    const response = await this.client.post<ApiResponse<OrderSchedule>>('/order-schedules', data);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke oprette ordreskema');
  }

  async updateOrderSchedule(id: string, data: OrderScheduleInput): Promise<OrderSchedule> {
    const response = await this.client.put<ApiResponse<OrderSchedule>>(`/order-schedules/${id}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke opdatere ordreskema');
  }

  async deleteOrderSchedule(id: string): Promise<void> {
    await this.client.delete(`/order-schedules/${id}`);
  }

  async pauseOrderSchedule(id: string): Promise<OrderSchedule> {
    const response = await this.client.post<ApiResponse<OrderSchedule>>(`/order-schedules/${id}/pause`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke sætte ordreskema på pause');
  }

  async resumeOrderSchedule(id: string): Promise<OrderSchedule> {
    const response = await this.client.post<ApiResponse<OrderSchedule>>(`/order-schedules/${id}/resume`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke genoptage ordreskema');
  }

  async triggerOrderSchedule(id: string): Promise<{ created: number; failed: number; errors: string[] }> {
    const response = await this.client.post<ApiResponse<{ created: number; failed: number; errors: string[] }>>(`/order-schedules/${id}/trigger`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke køre ordreskema');
  }

  async getOrderScheduleCustomers(): Promise<Array<{ client: string; clientNumber: string }>> {
    const response = await this.client.get('/order-schedules/customers');
    return response.data.data || [];
  }

  async getOrderScheduleTeams(): Promise<Array<{ id: string; name: string }>> {
    const response = await this.client.get('/order-schedules/teams');
    return response.data.data || [];
  }

  async getOrderScheduleLabels(): Promise<Array<{ id: string; description: string; color: string }>> {
    const response = await this.client.get('/order-schedules/labels');
    return response.data.data || [];
  }

  async getOrderScheduleProducts(): Promise<{
    products: Array<{ id: string; productNumber: string; name: string; salesPrice: string | null }>;
    surcharges: Array<{ id: string; productNumber: string; name: string; percentage: string | null }>;
  }> {
    const response = await this.client.get('/order-schedules/products');
    return response.data.data || { products: [], surcharges: [] };
  }

  // Order Templates (within a schedule)
  async getOrderTemplates(scheduleId: string): Promise<OrderTemplate[]> {
    const response = await this.client.get<ApiResponse<OrderTemplate[]>>(`/order-schedules/${scheduleId}/templates`);
    return response.data.data || [];
  }

  async getOrderTemplate(scheduleId: string, templateId: string): Promise<OrderTemplate> {
    const response = await this.client.get<ApiResponse<OrderTemplate>>(`/order-schedules/${scheduleId}/templates/${templateId}`);
    if (response.data.data) return response.data.data;
    throw new Error('Template not found');
  }

  async createOrderTemplate(scheduleId: string, data: OrderTemplateInput): Promise<OrderTemplate> {
    const response = await this.client.post<ApiResponse<OrderTemplate>>(`/order-schedules/${scheduleId}/templates`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to create template');
  }

  async updateOrderTemplate(scheduleId: string, templateId: string, data: OrderTemplateInput): Promise<OrderTemplate> {
    const response = await this.client.put<ApiResponse<OrderTemplate>>(`/order-schedules/${scheduleId}/templates/${templateId}`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Failed to update template');
  }

  async deleteOrderTemplate(scheduleId: string, templateId: string): Promise<void> {
    await this.client.delete(`/order-schedules/${scheduleId}/templates/${templateId}`);
  }

  // e-conomic Integration
  async testEconomicConnection(): Promise<{ success: boolean; message: string }> {
    const response = await this.client.get<ApiResponse<{ success: boolean; message: string }>>('/economic/test-connection');
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke teste forbindelse');
  }

  async syncEconomicInvoices(from: string, to: string): Promise<{ synced: number; total: number }> {
    const response = await this.client.post<ApiResponse<{ synced: number; total: number }>>('/economic/sync-invoices', { from, to });
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke synkronisere fakturaer');
  }

  async getEconomicInvoices(params?: { from?: string; to?: string; matchStatus?: string }): Promise<EconomicInvoice[]> {
    const response = await this.client.get<ApiResponse<EconomicInvoice[]>>('/economic/invoices', { params });
    return response.data.data || [];
  }

  async confirmEconomicMatch(id: string): Promise<EconomicInvoice> {
    const response = await this.client.put<ApiResponse<EconomicInvoice>>(`/economic/invoices/${id}/confirm`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke bekræfte match');
  }

  async linkEconomicInvoice(id: string, data: { trailerId?: string; routeId?: string; cargoId?: string; costCategoryKey?: string }): Promise<EconomicInvoice> {
    const response = await this.client.put<ApiResponse<EconomicInvoice>>(`/economic/invoices/${id}/link`, data);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke tilknytte faktura');
  }

  async ignoreEconomicInvoice(id: string): Promise<EconomicInvoice> {
    const response = await this.client.put<ApiResponse<EconomicInvoice>>(`/economic/invoices/${id}/ignore`);
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke ignorere faktura');
  }

  async getEconomicSuppliers(): Promise<EconomicSupplier[]> {
    const response = await this.client.get<ApiResponse<EconomicSupplier[]>>('/economic/suppliers');
    return response.data.data || [];
  }

  async getEconomicAccounts(): Promise<Array<{ accountNumber: number; name: string }>> {
    const response = await this.client.get<ApiResponse<Array<{ accountNumber: number; name: string }>>>('/economic/accounts');
    return response.data.data || [];
  }

  async getEconomicJournals(): Promise<Array<{ journalNumber: number; name: string }>> {
    const response = await this.client.get<ApiResponse<Array<{ journalNumber: number; name: string }>>>('/economic/journals');
    return response.data.data || [];
  }

  async pushCostsToEconomic(manualCostIds: string[]): Promise<{ pushed: number; failed: number }> {
    const response = await this.client.post<ApiResponse<{ pushed: number; failed: number }>>('/economic/push-costs', { manualCostIds });
    if (response.data.data) return response.data.data;
    throw new Error('Kunne ikke pushe omkostninger');
  }

  async getEconomicJournalEntries(): Promise<EconomicJournalEntry[]> {
    const response = await this.client.get<ApiResponse<EconomicJournalEntry[]>>('/economic/journal-entries');
    return response.data.data || [];
  }

  // ============ Payroll ============
  async getPayrollCatalogs(): Promise<any[]> {
    const response = await this.client.get('/payroll/catalogs');
    return response.data.data || [];
  }

  async getPayrollRuleSets(): Promise<any[]> {
    const response = await this.client.get('/payroll/rulesets');
    return response.data.data || [];
  }

  async createInheritedPayrollRuleSet(catalogKey: string, name: string, isDefault: boolean): Promise<any> {
    const response = await this.client.post('/payroll/rulesets/inherit', { catalogKey, name, isDefault });
    return response.data.data;
  }

  async clonePayrollRuleSet(id: string): Promise<any> {
    const response = await this.client.post(`/payroll/rulesets/${id}/clone`);
    return response.data.data;
  }

  async updatePayrollRuleSet(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/payroll/rulesets/${id}`, data);
    return response.data.data;
  }

  async updatePayrollRuleSetRate(ruleSetId: string, rateId: string, data: any): Promise<any> {
    const response = await this.client.put(`/payroll/rulesets/${ruleSetId}/rates/${rateId}`, data);
    return response.data.data;
  }

  async deletePayrollRuleSet(id: string): Promise<void> {
    await this.client.delete(`/payroll/rulesets/${id}`);
  }

  async getDriverPayrollProfiles(): Promise<any[]> {
    const response = await this.client.get('/payroll/profiles');
    return response.data.data || [];
  }

  async upsertDriverPayrollProfile(driverId: string, data: any): Promise<any> {
    const response = await this.client.put(`/payroll/profiles/${driverId}`, data);
    return response.data.data;
  }

  async deleteDriverPayrollProfile(driverId: string): Promise<void> {
    await this.client.delete(`/payroll/profiles/${driverId}`);
  }

  async getPayrollBreakdown(driverId: string, from: string, to: string): Promise<any> {
    const response = await this.client.get('/payroll/breakdown', { params: { driverId, from, to } });
    return response.data.data;
  }

  async getPayrollBreakdownAll(from: string, to: string): Promise<any[]> {
    const response = await this.client.get('/payroll/breakdown/all', { params: { from, to } });
    return response.data.data || [];
  }

  // ---- Approver groups + payroll approval batches ----
  async getApproverGroups(): Promise<Array<{ id: string; name: string; approverEmail: string; approverName: string | null; driverIds: string[] }>> {
    const r = await this.client.get('/payroll/approver-groups');
    return r.data.data || [];
  }
  async createApproverGroup(data: { name: string; approverEmail: string; approverName?: string | null; driverIds: string[] }): Promise<any> {
    const r = await this.client.post('/payroll/approver-groups', data);
    return r.data.data;
  }
  async updateApproverGroup(id: string, data: { name: string; approverEmail: string; approverName?: string | null; driverIds: string[] }): Promise<any> {
    const r = await this.client.put(`/payroll/approver-groups/${id}`, data);
    return r.data.data;
  }
  async deleteApproverGroup(id: string): Promise<void> {
    await this.client.delete(`/payroll/approver-groups/${id}`);
  }

  async createPayrollApprovalBatch(data: {
    periodStart: string; periodEnd: string;
    approverEmail: string; approverName?: string | null;
    groupId?: string | null; driverIds: string[];
  }): Promise<{ batchId: string; items: Array<{ id: string; driverName: string; token: string }> }> {
    const r = await this.client.post('/payroll/approval-batches', data);
    return r.data.data;
  }
  async getPayrollApprovalBatches(): Promise<any[]> {
    const r = await this.client.get('/payroll/approval-batches');
    return r.data.data || [];
  }
  async downloadPayrollApprovalEml(batchId: string): Promise<void> {
    const response = await this.client.get(`/payroll/approval-batches/${batchId}/eml`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'message/rfc822' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `Lonoverblik-godkendelse-${batchId.slice(0, 8)}.eml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  async getPayrollManualEntries(from?: string, to?: string, driverId?: string): Promise<any[]> {
    const response = await this.client.get('/payroll/manual-entries', { params: { from, to, driverId } });
    return response.data.data || [];
  }

  async createPayrollManualEntry(data: any): Promise<any> {
    const response = await this.client.post('/payroll/manual-entries', data);
    return response.data.data;
  }

  async updatePayrollManualEntry(id: string, data: any): Promise<any> {
    const response = await this.client.put(`/payroll/manual-entries/${id}`, data);
    return response.data.data;
  }

  async deletePayrollManualEntry(id: string): Promise<void> {
    await this.client.delete(`/payroll/manual-entries/${id}`);
  }

  async lockPayrollPeriod(data: { driverId: string; periodStart: string; periodEnd: string; periodType?: 'WEEK' | 'MONTH' }): Promise<any> {
    const response = await this.client.post('/payroll/snapshots', data);
    return response.data.data;
  }

  async getPayrollSnapshots(from?: string, to?: string): Promise<any[]> {
    const response = await this.client.get('/payroll/snapshots', { params: { from, to } });
    return response.data.data || [];
  }

  async deletePayrollSnapshot(id: string): Promise<void> {
    await this.client.delete(`/payroll/snapshots/${id}`);
  }

  async downloadPayrollCsv(from: string, to: string): Promise<void> {
    const response = await this.client.get('/payroll/export/csv', {
      params: { from, to },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `loenoverblik-${from}-${to}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }

  // --- Produktsalg / sales locations ---

  async getSalesLocations(): Promise<import('../types').SalesLocation[]> {
    const r = await this.client.get('/sales-locations');
    return r.data.data || [];
  }

  async getSalesLocation(id: string): Promise<import('../types').SalesLocation> {
    const r = await this.client.get(`/sales-locations/${id}`);
    return r.data.data;
  }

  async createSalesLocation(data: import('../types').SalesLocationInput): Promise<import('../types').SalesLocation> {
    const r = await this.client.post('/sales-locations', data);
    return r.data.data;
  }

  async updateSalesLocation(id: string, data: Partial<import('../types').SalesLocationInput>): Promise<import('../types').SalesLocation> {
    const r = await this.client.put(`/sales-locations/${id}`, data);
    return r.data.data;
  }

  async deleteSalesLocation(id: string): Promise<void> {
    await this.client.delete(`/sales-locations/${id}`);
  }

  async addSalesLocationProduct(locationId: string, data: import('../types').SalesLocationProductInput): Promise<import('../types').SalesLocationProduct> {
    const r = await this.client.post(`/sales-locations/${locationId}/products`, data);
    return r.data.data;
  }

  async updateSalesLocationProduct(locationId: string, productId: string, data: Partial<import('../types').SalesLocationProductInput>): Promise<import('../types').SalesLocationProduct> {
    const r = await this.client.put(`/sales-locations/${locationId}/products/${productId}`, data);
    return r.data.data;
  }

  async deleteSalesLocationProduct(locationId: string, productId: string): Promise<void> {
    await this.client.delete(`/sales-locations/${locationId}/products/${productId}`);
  }

  async resolveProductPrice(args: {
    doraProductId: string;
    customerNumber?: string | null;
    salesLocationId?: string | null;
  }): Promise<{
    price: number | null;
    costPrice: number | null;
    unitName: string | null;
    source: 'CUSTOMER' | 'LOCATION' | 'NONE';
    salesLocationName?: string;
    sourceQuoteId?: string | null;
  }> {
    const r = await this.client.get('/sales-locations/resolve-price', { params: args });
    return r.data.data;
  }

  async getCustomerPrices(): Promise<Array<{
    id: string;
    organizationId: string;
    salesLocationId: string;
    salesLocationName: string;
    doraProductId: string;
    doraProductNumber: string;
    doraProductName: string;
    customerNumber: string;
    price: number;
    costPrice: number | null;
    unitName: string | null;
    sourceQuoteId: string | null;
    createdAt: string;
    updatedAt: string;
  }>> {
    const r = await this.client.get('/sales-locations/customer-prices');
    return r.data.data || [];
  }

  async upsertCustomerPriceBulk(input: {
    customerNumber: string;
    doraProductId: string;
    doraProductNumber: string;
    doraProductName: string;
    price: number;
    unitName?: string | null;
    salesLocationIds?: string[];
    allLocations?: boolean;
  }): Promise<{ upserted: number }> {
    const r = await this.client.put<ApiResponse<{ upserted: number }>>('/sales-locations/customer-prices/bulk', input);
    return r.data.data || { upserted: 0 };
  }

  async deleteCustomerPriceBulk(customerNumber: string, doraProductId: string): Promise<{ deleted: number }> {
    const r = await this.client.delete<ApiResponse<{ deleted: number }>>('/sales-locations/customer-prices/bulk', {
      data: { customerNumber, doraProductId },
    });
    return r.data.data || { deleted: 0 };
  }

  async deleteCustomerPrice(id: string): Promise<void> {
    await this.client.delete(`/sales-locations/customer-prices/${id}`);
  }

  async getSalesLocationsReport(from: string, to: string): Promise<{
    from: string;
    to: string;
    cargoCount: number;
    totalCargos: number;
    locations: Array<{
      locationId: string;
      name: string;
      type: string;
      postcode: string | null;
      city: string | null;
      cargoCount: number;
      cargos: Array<{
        id: string;
        ref: string;
        customerNumber: string | null;
        firstStopDate: string;
        firstStopCity: string | null;
        lastStopCity: string | null;
        type: string;
      }>;
    }>;
  }> {
    const r = await this.client.get('/sales-locations/report', { params: { from, to } });
    return r.data.data;
  }

  // ============ Price Groups ============
  async getPriceGroups(): Promise<import('../types').PriceGroup[]> {
    const r = await this.client.get<ApiResponse<import('../types').PriceGroup[]>>('/price-groups');
    return r.data.data || [];
  }
  async getPriceGroup(id: string): Promise<import('../types').PriceGroup> {
    const r = await this.client.get<ApiResponse<import('../types').PriceGroup>>(`/price-groups/${id}`);
    if (!r.data.data) throw new Error('Prisgruppe ikke fundet');
    return r.data.data;
  }
  async createPriceGroup(input: import('../types').PriceGroupInput): Promise<import('../types').PriceGroup> {
    const r = await this.client.post<ApiResponse<import('../types').PriceGroup>>('/price-groups', input);
    if (!r.data.data) throw new Error('Kunne ikke oprette prisgruppe');
    return r.data.data;
  }
  async updatePriceGroup(id: string, input: Partial<import('../types').PriceGroupInput>): Promise<import('../types').PriceGroup> {
    const r = await this.client.put<ApiResponse<import('../types').PriceGroup>>(`/price-groups/${id}`, input);
    if (!r.data.data) throw new Error('Kunne ikke opdatere prisgruppe');
    return r.data.data;
  }
  async deletePriceGroup(id: string): Promise<void> {
    await this.client.delete(`/price-groups/${id}`);
  }
  async upsertPriceGroupPrice(groupId: string, salesLocationProductId: string, price: number): Promise<void> {
    await this.client.put(`/price-groups/${groupId}/prices`, { salesLocationProductId, price });
  }
  async deletePriceGroupPrice(groupId: string, priceId: string): Promise<void> {
    await this.client.delete(`/price-groups/${groupId}/prices/${priceId}`);
  }
  async addPriceGroupCustomers(groupId: string, customerNumbers: string[]): Promise<import('../types').PriceGroup> {
    const r = await this.client.post<ApiResponse<import('../types').PriceGroup>>(`/price-groups/${groupId}/customers`, { customerNumbers });
    if (!r.data.data) throw new Error('Kunne ikke tilføje kunder');
    return r.data.data;
  }
  async removePriceGroupCustomer(groupId: string, customerNumber: string): Promise<import('../types').PriceGroup> {
    const r = await this.client.delete<ApiResponse<import('../types').PriceGroup>>(`/price-groups/${groupId}/customers/${customerNumber}`);
    if (!r.data.data) throw new Error('Kunne ikke fjerne kunde');
    return r.data.data;
  }

  // ============ Sales-location merges ============
  async autoMatchSalesLocationToDora(locId: string): Promise<{ matched: number; ambiguous: number; unmatched: number; alreadyMapped: number }> {
    const r = await this.client.post<ApiResponse<{ matched: number; ambiguous: number; unmatched: number; alreadyMapped: number }>>(`/sales-locations/${locId}/auto-match-dora`);
    return r.data.data || { matched: 0, ambiguous: 0, unmatched: 0, alreadyMapped: 0 };
  }
  async mergeSalesLocation(sourceId: string, targetId: string): Promise<{ movedProducts: number }> {
    const r = await this.client.post<ApiResponse<{ merged: { movedProducts: number } }>>(`/sales-locations/${sourceId}/merge-into/${targetId}`);
    return r.data.data?.merged || { movedProducts: 0 };
  }
  async mergeSalesLocationProduct(locId: string, sourceId: string, targetId: string): Promise<{ moved: number; mergedTie: number }> {
    const r = await this.client.post<ApiResponse<{ moved: number; mergedTie: number }>>(`/sales-locations/${locId}/products/${sourceId}/merge-into/${targetId}`);
    return r.data.data || { moved: 0, mergedTie: 0 };
  }

  // ===== Emballagekompensation =====

  async getEmballageStatus(): Promise<EmballageStatus> {
    const r = await this.client.get<ApiResponse<EmballageStatus>>('/emballage/status');
    return r.data.data || { enabled: false, customerCount: 0, enrolledCount: 0, productCount: 0, facilityCount: 0 };
  }

  async getEmballageFraktioner(): Promise<EmballageFraktionDef[]> {
    const r = await this.client.get<ApiResponse<EmballageFraktionDef[]>>('/emballage/fraktioner');
    return r.data.data || [];
  }

  async getEmballageCustomers(): Promise<EmballageCustomer[]> {
    const r = await this.client.get<ApiResponse<EmballageCustomer[]>>('/emballage/customers');
    return r.data.data || [];
  }

  async syncEmballageCustomers(): Promise<{ created: number; updated: number }> {
    const r = await this.client.post<ApiResponse<{ created: number; updated: number }>>('/emballage/customers/sync');
    return r.data.data || { created: 0, updated: 0 };
  }

  async updateEmballageCustomer(id: string, data: Partial<EmballageCustomer>): Promise<EmballageCustomer> {
    const r = await this.client.patch<ApiResponse<EmballageCustomer>>(`/emballage/customers/${id}`, data);
    return r.data.data!;
  }

  async setEmballageEnrollment(ids: string[], enrolled: boolean): Promise<number> {
    const r = await this.client.put<ApiResponse<{ updated: number }>>('/emballage/customers/enrollment', { ids, enrolled });
    return r.data.data?.updated ?? 0;
  }

  async getEmballageProducts(): Promise<EmballageProduct[]> {
    const r = await this.client.get<ApiResponse<EmballageProduct[]>>('/emballage/products');
    return r.data.data || [];
  }

  async syncEmballageProducts(): Promise<{ created: number; updated: number; autoMapped: number }> {
    const r = await this.client.post<ApiResponse<{ created: number; updated: number; autoMapped: number }>>('/emballage/products/sync');
    return r.data.data || { created: 0, updated: 0, autoMapped: 0 };
  }

  async updateEmballageProduct(id: string, data: { fraction?: string | null; included?: boolean }): Promise<EmballageProduct> {
    const r = await this.client.patch<ApiResponse<EmballageProduct>>(`/emballage/products/${id}`, data);
    return r.data.data!;
  }

  async getEmballageFacilities(): Promise<EmballageFacility[]> {
    const r = await this.client.get<ApiResponse<EmballageFacility[]>>('/emballage/facilities');
    return r.data.data || [];
  }

  async createEmballageFacility(data: Partial<EmballageFacility>): Promise<EmballageFacility> {
    const r = await this.client.post<ApiResponse<EmballageFacility>>('/emballage/facilities', data);
    return r.data.data!;
  }

  async updateEmballageFacility(id: string, data: Partial<EmballageFacility>): Promise<EmballageFacility> {
    const r = await this.client.put<ApiResponse<EmballageFacility>>(`/emballage/facilities/${id}`, data);
    return r.data.data!;
  }

  async deleteEmballageFacility(id: string): Promise<void> {
    await this.client.delete(`/emballage/facilities/${id}`);
  }

  /// Starter rapporten som et job og svarer med det samme. Arbejdet kan tage
  /// minutter, så klienten følger status med getEmballageReport.
  async startEmballageReport(from: string, to: string, facilityId: string): Promise<EmballageSavedReport> {
    const r = await this.client.post<ApiResponse<EmballageSavedReport>>('/emballage/report', { from, to, facilityId });
    return r.data.data!;
  }

  async getEmballageReport(id: string): Promise<EmballageSavedReport> {
    const r = await this.client.get<ApiResponse<EmballageSavedReport>>(`/emballage/reports/${id}`);
    return r.data.data!;
  }

  async getEmballageReports(): Promise<EmballageSavedReport[]> {
    const r = await this.client.get<ApiResponse<EmballageSavedReport[]>>('/emballage/reports');
    return r.data.data || [];
  }

  async deleteEmballageReport(id: string): Promise<void> {
    await this.client.delete(`/emballage/reports/${id}`);
  }

  async downloadEmballageReportPdf(id: string, filename: string): Promise<void> {
    const response = await this.client.get(`/emballage/reports/${id}/pdf`, { responseType: 'blob' });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 10000);
  }
}

export const api = new ApiService();

// Public quote API (no auth)
const publicClient = axios.create({ baseURL: '/api/public/quotes' });

export async function getPublicQuote(token: string): Promise<PublicQuoteView> {
  const response = await publicClient.get<ApiResponse<PublicQuoteView>>(`/${token}`);
  if (response.data.data) return response.data.data;
  throw new Error('Tilbud ikke fundet');
}

export async function acceptPublicQuote(token: string, comment?: string | null): Promise<void> {
  await publicClient.post(`/${token}/accept`, { comment });
}

export async function rejectPublicQuote(token: string, comment?: string | null): Promise<void> {
  await publicClient.post(`/${token}/reject`, { comment });
}

export async function downloadPublicQuotePdf(token: string, quoteNumber: number): Promise<void> {
  const response = await publicClient.get(`/${token}/pdf`, { responseType: 'blob' });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `Tilbud-${quoteNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => window.URL.revokeObjectURL(url), 10000);
}
